import { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { Refresh2 } from "iconsax-react";
import { ModalWrapper, Select, DatePicker } from "@/shared/ui";
import { AuditEntryItem, Pagination } from "@/shared/components";
import { useScrollDetection } from "@/shared/hooks";
import { type Locale, formatDateForApi } from "@/shared/utils";
import type { AuditEntryItemConfig } from "@/shared/components";
import { buildTasksAuditConfig, isReorderEntry } from "@/features/tasks/utils/auditHelpers";
import { useGetProjectAuditQuery } from "../api";
import { renderBudgetAuditMetadata } from "../utils/budgetAuditHelpers";
import type { AuditQueryParams } from "../types";
import HistoryListSkeleton from "./HistoryListSkeleton";

interface Props {
  projectId: number;
  onClose: () => void;
  locale: Locale;
}

const PAGE_SIZE = 20;

const ACTION_OPTIONS = [
  { value: "", label: "history.filterAllActions" },
  { value: "task.created", label: "history.actionCategory.task.created" },
  { value: "task.updated", label: "history.actionCategory.task.updated" },
  { value: "task.status_changed", label: "history.actionCategory.task.status_changed" },
  { value: "task.deleted", label: "history.actionCategory.task.deleted" },
  { value: "taskcomment.created", label: "history.actionCategory.taskcomment.created" },
  { value: "taskcomment.deleted", label: "history.actionCategory.taskcomment.deleted" },
  { value: "project.created", label: "history.actionCategory.project.created" },
  { value: "project.updated", label: "history.actionCategory.project.updated" },
  { value: "project.redistributed", label: "history.actionCategory.project.redistributed" },
  { value: "project.income_source_added", label: "history.actionCategory.project.income_source_added" },
  { value: "project.income_source_returned", label: "history.actionCategory.project.income_source_returned" },
] as const;

export default function ProjectHistoryModal({ projectId, onClose, locale }: Props) {
  const { t } = useTranslation("ProjectsPage");
  const { t: tTasks } = useTranslation("TasksPage");

  const auditConfig = useMemo<AuditEntryItemConfig>(
    function buildAuditConfig() {
      const base = buildTasksAuditConfig(tTasks, t, locale);
      return {
        ...base,
        renderMetadata(entry, tFn) {
          const budgetMetadata = renderBudgetAuditMetadata(entry, t, locale);
          if (budgetMetadata) return budgetMetadata;
          return base.renderMetadata?.(entry, tFn) ?? null;
        },
      };
    },
    [tTasks, t, locale],
  );

  const [actionFilter, setActionFilter] = useState<string>("");
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [page, setPage] = useState(1);

  const queryParams = useMemo<AuditQueryParams>(() => {
    const params: AuditQueryParams = {
      page,
      page_size: PAGE_SIZE,
      project: projectId,
    };
    if (actionFilter) params.action = actionFilter;
    if (dateFrom) params.date_from = formatDateForApi(dateFrom);
    if (dateTo) params.date_to = formatDateForApi(dateTo);
    return params;
  }, [projectId, page, actionFilter, dateFrom, dateTo]);

  const { data, isFetching } = useGetProjectAuditQuery(queryParams);

  const totalPages = Math.ceil((data?.count ?? 0) / PAGE_SIZE);
  const fromItem = data && data.count > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const toItem = data ? Math.min(page * PAGE_SIZE, data.count) : 0;

  const { scrollRef, hasScroll } = useScrollDetection();

  const actionOptions = useMemo(() => ACTION_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.label) })), [t]);

  function handleReset() {
    setActionFilter("");
    setDateFrom(null);
    setDateTo(null);
    setPage(1);
  }

  function handlePageChange(newPage: number) {
    setPage(newPage);
  }

  function handleActionChange(value: string | null) {
    setActionFilter(value ?? "");
    setPage(1);
  }

  function handleDateFromChange(value: Date | null) {
    setDateFrom(value);
    setPage(1);
  }

  function handleDateToChange(value: Date | null) {
    setDateTo(value);
    setPage(1);
  }

  const entries = (data?.results ?? []).filter((e) => !isReorderEntry(e));
  const hasFilters = actionFilter !== "" || dateFrom !== null || dateTo !== null;

  return (
    <ModalWrapper onClose={onClose} width="w-5xl">
      <h4 className="text-display-2xs content-base-primary">{t("history.title")}</h4>

      <div className={cn("grid gap-3 mt-2", hasFilters ? "grid-cols-4" : "grid-cols-3")}>
        <Select
          label={t("history.filterAction")}
          options={actionOptions}
          value={actionFilter}
          onChange={handleActionChange}
          placeholder={t("history.filterAllActions")}
        />
        <DatePicker
          label={t("history.filterDateFrom")}
          value={dateFrom}
          onChange={handleDateFromChange}
          locale={locale}
          placeholder={t("history.filterDatePlaceholder")}
        />
        <DatePicker
          label={t("history.filterDateTo")}
          value={dateTo}
          onChange={handleDateToChange}
          locale={locale}
          placeholder={t("history.filterDatePlaceholder")}
          minDate={dateFrom ?? undefined}
        />
        {hasFilters && (
          <button
            type="button"
            className="px-3 h-10 flex justify-center items-center gap-2 content-base-low cursor-pointer self-end"
            onClick={handleReset}>
            <Refresh2 size={16} color="currentColor" />
            <span className="text-label-medium">{t("history.filterReset")}</span>
          </button>
        )}
      </div>

      <div className="border-t surface-base-stroke mt-2" />

      {isFetching ? (
        <HistoryListSkeleton />
      ) : entries.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-body-regular-md content-base-secondary">{t("history.empty")}</p>
        </div>
      ) : (
        <>
          <div ref={scrollRef} className={cn("mt-4 max-h-[400px] overflow-auto page-scroll", hasScroll && "pr-3")}>
            {entries.map((entry, index) => (
              <AuditEntryItem
                key={entry.id}
                entry={entry}
                isLast={index === entries.length - 1}
                namespace="history"
                config={auditConfig}
                t={tTasks}
                locale={locale}
              />
            ))}
          </div>

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              fromItem={fromItem}
              toItem={toItem}
              total={data?.count ?? 0}
            />
          )}
        </>
      )}
    </ModalWrapper>
  );
}
